import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../api/client'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const profile = ref(null)
  const userEmail = ref(null)
  const userHashId = ref(null)
  const isAuthenticated = ref(false)

  const userHashIdComputed = computed(() => userHashId.value)

  const initializeFromStorage = () => {
    const storedEmail = localStorage.getItem('userEmail')
    const storedHashId = localStorage.getItem('userHashId')
    if (storedEmail && storedHashId) {
      userEmail.value = storedEmail
      userHashId.value = storedHashId
      isAuthenticated.value = true
      user.value = {
        email: storedEmail,
        hashid: storedHashId,
      }
    }
  }

  const loginWithEmailAndHashId = async (email, hashid) => {
    try {
      const response = await api.post('/users/validate/', {
        email: email.trim(),
        hashid: hashid.trim(),
      })

      userEmail.value = email
      userHashId.value = hashid
      isAuthenticated.value = true
      user.value = response.data
      localStorage.setItem('userEmail', email)
      localStorage.setItem('userHashId', hashid)
      return true
    } catch (error) {
      console.error('Authentication failed:', error)
      throw new Error(error.response?.data?.detail || 'Invalid email or project ID')
    }
  }

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login/', { username, password })
      user.value = response.data.user
      profile.value = response.data.profile
      isAuthenticated.value = true
      return true
    } catch (error) {
      console.error('Login failed:', error)
      return false
    }
  }

  const logout = async () => {
    const tempEmail = userEmail.value
    const tempHashId = userHashId.value

    try {
      await api.post('/auth/logout/')
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      user.value = null
      profile.value = null
      userEmail.value = null
      userHashId.value = null
      isAuthenticated.value = false
      localStorage.removeItem('userEmail')
      localStorage.removeItem('userHashId')

      // Send usage log before clearing auth
      if (tempEmail) {
        try {
          import('../services/usageTracker').then(({ default: usageTracker }) => {
            usageTracker.endSession(tempEmail, tempHashId)
          })
        } catch (error) {
          console.error('Failed to send usage log:', error)
        }
      }
    }
  }

  const getCurrentUser = async () => {
    try {
      const response = await api.get('/users/me/')
      user.value = response.data
      isAuthenticated.value = true
      return response.data
    } catch (error) {
      isAuthenticated.value = false
    }
  }

  const getProfile = async () => {
    try {
      const response = await api.get('/profiles/my_profile/')
      profile.value = response.data
      return response.data
    } catch (error) {
      console.error('Failed to fetch profile:', error)
    }
  }

  const updateProfile = async (data) => {
    try {
      const response = await api.patch('/profiles/update_profile/', data)
      profile.value = response.data
      return response.data
    } catch (error) {
      console.error('Failed to update profile:', error)
      throw error
    }
  }

  const setGuestMode = () => {
    user.value = { username: 'User', first_name: 'User', role: 'Data & AI Project Manager' }
    isAuthenticated.value = false
  }

  return {
    user,
    profile,
    userEmail,
    userHashId,
    userHashIdComputed,
    isAuthenticated,
    initializeFromStorage,
    loginWithEmailAndHashId,
    login,
    logout,
    getCurrentUser,
    getProfile,
    updateProfile,
    setGuestMode,
  }
})
